import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Legal.css';

const PolitiqueConfidentialite = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const sections = [
    { id: 'engagement', title: 'Notre engagement' },
    { id: 'collecte', title: 'Données collectées' },
    { id: 'finalites', title: 'Finalités du traitement' },
    { id: 'base-legale', title: 'Base légale' },
    { id: 'conservation', title: 'Durée de conservation' },
    { id: 'partage', title: 'Partage des données' },
    { id: 'securite', title: 'Sécurité' },
    { id: 'droits', title: 'Vos droits' },
    { id: 'cookies', title: 'Cookies' },
    { id: 'contact', title: 'Nous contacter' },
  ];

  return (
    <div className="legal-page">
      <section className="legal-hero">
        <div className="container">
          <span className="legal-label">Protection des données</span>
          <h1>Politique de <span className="hdr-green">confidentialité</span></h1>
          <p>
            Votre vie privée est importante pour nous. Cette politique décrit comment
            DOMOELEC collecte, utilise et protège vos données personnelles lorsque vous
            utilisez notre site ou nos services.
          </p>
          <div className="legal-hero__meta">
            <span className="legal-hero__meta-item">
              <i className="fas fa-calendar-alt"></i>
              Dernière mise à jour : 21 avril 2026
            </span>
            <span className="legal-hero__meta-item">
              <i className="fas fa-shield-alt"></i>
              Conforme Loi 09-08
            </span>
          </div>
        </div>
      </section>

      <div className="legal-layout">
        <aside className="legal-toc">
          <p className="legal-toc__title">Sommaire</p>
          <ul>
            {sections.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`}>{s.title}</a>
              </li>
            ))}
          </ul>
        </aside>

        <main className="legal-content">
          <section id="engagement" className="legal-section">
            <h2><span className="legal-num">1</span>Notre engagement</h2>
            <p>
              <strong>DOMOELEC SARL</strong>, responsable du traitement, s'engage à protéger
              la vie privée et les données personnelles des visiteurs et clients de son site
              <strong> www.domoelec.ma</strong>.
            </p>
            <p>
              La présente politique de confidentialité s'inscrit dans le cadre de la
              <strong> loi marocaine 09-08</strong> relative à la protection des personnes
              physiques à l'égard du traitement des données à caractère personnel.
            </p>
            <div className="legal-info-card">
              <i className="fas fa-lock"></i>
              <p>
                Nous ne vendons jamais vos données personnelles à des tiers. Elles sont
                utilisées uniquement dans le cadre de notre relation commerciale ou de
                nos obligations légales.
              </p>
            </div>
          </section>

          <section id="collecte" className="legal-section">
            <h2><span className="legal-num">2</span>Données collectées</h2>
            <p>
              Nous collectons uniquement les données strictement nécessaires aux finalités
              décrites ci-dessous.
            </p>
            <h3>Données d'identification</h3>
            <ul>
              <li>Nom et prénom</li>
              <li>Raison sociale (pour les professionnels)</li>
              <li>Adresse email</li>
              <li>Numéro de téléphone</li>
              <li>Adresse postale (si demande de devis ou de livraison)</li>
            </ul>
            <h3>Données de navigation</h3>
            <ul>
              <li>Adresse IP</li>
              <li>Type et version du navigateur</li>
              <li>Pages consultées et durée de visite</li>
              <li>Données de cookies (voir section dédiée)</li>
            </ul>
          </section>

          <section id="finalites" className="legal-section">
            <h2><span className="legal-num">3</span>Finalités du traitement</h2>
            <p>Vos données sont traitées pour les finalités suivantes :</p>
            <ul>
              <li>Répondre à vos demandes de contact, de devis ou d'information</li>
              <li>Assurer le suivi commercial et la gestion de nos clients</li>
              <li>Gérer l'accès à l'espace administrateur et aux comptes autorisés</li>
              <li>Améliorer la qualité de notre site et de nos services</li>
              <li>Respecter nos obligations légales et comptables</li>
              <li>Vous envoyer, avec votre accord, des informations commerciales ciblées</li>
            </ul>
          </section>

          <section id="base-legale" className="legal-section">
            <h2><span className="legal-num">4</span>Base légale du traitement</h2>
            <p>Les traitements que nous effectuons reposent sur l'une des bases légales suivantes :</p>
            <ul>
              <li><strong>Votre consentement</strong> — pour les communications commerciales et les cookies non essentiels</li>
              <li><strong>L'exécution d'un contrat</strong> — pour la gestion des devis, commandes et prestations</li>
              <li><strong>Notre intérêt légitime</strong> — pour la sécurité et l'amélioration de notre site</li>
              <li><strong>Une obligation légale</strong> — pour la conservation des pièces comptables et administratives</li>
            </ul>
          </section>

          <section id="conservation" className="legal-section">
            <h2><span className="legal-num">5</span>Durée de conservation</h2>
            <p>
              Nous conservons vos données uniquement pendant la durée nécessaire aux
              finalités pour lesquelles elles ont été collectées :
            </p>
            <ul>
              <li><strong>Données clients :</strong> pendant toute la durée de la relation commerciale, puis 5 ans à des fins comptables</li>
              <li><strong>Demandes de devis / contact :</strong> 3 ans à compter du dernier échange</li>
              <li><strong>Prospects :</strong> 3 ans maximum après le dernier contact</li>
              <li><strong>Cookies et logs techniques :</strong> 13 mois maximum</li>
            </ul>
          </section>

          <section id="partage" className="legal-section">
            <h2><span className="legal-num">6</span>Partage des données</h2>
            <p>
              Vos données ne sont <strong>jamais vendues</strong>. Elles peuvent être partagées
              avec les catégories de destinataires suivantes, dans la stricte limite du
              nécessaire :
            </p>
            <ul>
              <li>Notre personnel habilité, tenu au secret professionnel</li>
              <li>Nos prestataires techniques (hébergeur, maintenance) sous contrat de confidentialité</li>
              <li>Nos transporteurs et partenaires logistiques, pour l'acheminement des produits</li>
              <li>Les autorités administratives ou judiciaires sur réquisition légale</li>
            </ul>
          </section>

          <section id="securite" className="legal-section">
            <h2><span className="legal-num">7</span>Sécurité des données</h2>
            <p>
              Nous mettons en œuvre des mesures techniques et organisationnelles appropriées
              pour protéger vos données contre la perte, l'altération, l'accès non autorisé
              ou la divulgation :
            </p>
            <ul>
              <li>Chiffrement des communications (HTTPS/SSL)</li>
              <li>Accès restreints à l'espace administrateur (mot de passe + code PIN)</li>
              <li>Sauvegardes régulières</li>
              <li>Formation du personnel à la sécurité</li>
            </ul>
          </section>

          <section id="droits" className="legal-section">
            <h2><span className="legal-num">8</span>Vos droits</h2>
            <p>
              Conformément à la loi 09-08, vous disposez des droits suivants sur vos données
              personnelles :
            </p>
            <ul>
              <li><strong>Droit d'accès :</strong> obtenir la confirmation que des données vous concernant sont traitées et en recevoir copie</li>
              <li><strong>Droit de rectification :</strong> corriger des données inexactes ou incomplètes</li>
              <li><strong>Droit d'opposition :</strong> vous opposer, pour motifs légitimes, à un traitement</li>
              <li><strong>Droit de suppression :</strong> demander l'effacement de vos données</li>
              <li><strong>Droit de retirer votre consentement :</strong> à tout moment, sans affecter la licéité des traitements antérieurs</li>
            </ul>
            <div className="legal-info-card">
              <i className="fas fa-info-circle"></i>
              <p>
                Pour exercer ces droits, contactez-nous à <a href="mailto:contact@domoelec.ma">contact@domoelec.ma</a>
                en précisant votre demande et en joignant un justificatif d'identité. Nous
                répondrons dans un délai maximal de 30 jours.
              </p>
            </div>
          </section>

          <section id="cookies" className="legal-section">
            <h2><span className="legal-num">9</span>Cookies</h2>
            <p>
              Notre site utilise différents types de cookies :
            </p>
            <h3>Cookies essentiels</h3>
            <p>Nécessaires au fonctionnement du site (authentification, préférences). Ils ne nécessitent pas votre consentement.</p>
            <h3>Cookies de mesure d'audience</h3>
            <p>Nous permettent de comprendre comment les visiteurs utilisent le site afin d'en améliorer l'ergonomie. Ils sont activés uniquement avec votre accord.</p>
            <h3>Gestion des cookies</h3>
            <p>
              Vous pouvez configurer votre navigateur (Chrome, Firefox, Safari, Edge) pour
              refuser tout ou partie des cookies, ou supprimer ceux déjà déposés. Pour plus
              d'informations, consultez l'aide de votre navigateur.
            </p>
          </section>

          <section id="contact" className="legal-section">
            <h2><span className="legal-num">10</span>Nous contacter</h2>
            <p>
              Pour toute question relative à la protection de vos données personnelles ou
              à l'exercice de vos droits, vous pouvez nous joindre :
            </p>
            <ul>
              <li><strong>Par email :</strong> <a href="mailto:contact@domoelec.ma">contact@domoelec.ma</a></li>
              <li><strong>Par téléphone :</strong> <a href="tel:0522340659">05 22 34 06 59</a></li>
              <li><strong>Par courrier :</strong> DOMOELEC SARL — 47 Rue Des Orangers, Ain Sebaa, Casablanca</li>
            </ul>
            <p>
              Les présentes mentions complètent nos <Link to="/mentions-legales">Mentions légales</Link>.
            </p>
          </section>

          <div className="legal-cta">
            <div className="legal-cta__text">
              <h3>Besoin d'exercer vos droits ?</h3>
              <p>Notre équipe traite votre demande sous 30 jours maximum.</p>
            </div>
            <a href="mailto:contact@domoelec.ma" className="legal-cta__btn">
              <i className="fas fa-user-shield"></i>
              Contacter DOMOELEC
            </a>
          </div>
        </main>
      </div>
    </div>
  );
};

export default PolitiqueConfidentialite;
